import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { Loader2, MessageSquare, Search } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSessionSearch } from "@/hooks/use-session-search";
import type { Session, SessionSearchResult } from "@/lib/session";
import type { Agent } from "@/hooks/use-agents";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return i18n.t("commandPalette.today", { ns: "chat" });
  if (diffDays === 1) return i18n.t("commandPalette.yesterday", { ns: "chat" });
  if (diffDays < 7) return i18n.t("commandPalette.daysAgo", { ns: "chat", count: diffDays });
  return d.toLocaleDateString(i18n.language, { month: "short", day: "numeric" });
}

/**
 * Highlight the matched query substring within a text snippet.
 *
 * Wraps the match in a `<mark>` element with a subtle background so the
 * user can see *why* this result matched.
 */
function highlightMatch(text: string, query: string, matchStart?: number | null, matchEnd?: number | null) {
  if (!query.trim()) return text;
  const safeOffset = matchStart != null && matchEnd != null && matchStart >= 0
    && matchEnd > matchStart && matchEnd <= text.length;
  const idx = safeOffset ? matchStart : text.toLowerCase().indexOf(query.toLowerCase());
  const end = safeOffset ? matchEnd : idx + query.length;
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200/60 rounded-sm px-0.5 dark:bg-yellow-500/30">
        {text.slice(idx, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  CommandPalette                                                     */
/* ------------------------------------------------------------------ */

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSession: (sessionId: string) => void;
  getToken: () => Promise<string>;
  agents: Agent[];
  /** Preloaded recent sessions for instant display when the palette opens
   *  with an empty query. Gives immediate keyboard navigation to recent
   *  conversations without waiting for a network round-trip. */
  recentSessions?: Session[];
  scopeKey?: string;
  useSearch?: typeof useSessionSearch;
}

/**
 * Discord-style Ctrl+K command palette for session search.
 *
 * Opens via `useCommandPalette` (global Cmd/Ctrl+K shortcut). Loaded session
 * titles match immediately. The hook then fetches unloaded titles through
 * the paginated sessions endpoint and user/assistant content through search.
 *
 * Results are rendered in two `CommandGroup`s:
 *  - **Sessions** — title matches (`match_type: "title"`)
 *  - **Messages** — content matches (`match_type: "content"`) with snippets
 *
 * The hook deduplicates the groups by session id, giving titles priority.
 *
 * When the query is empty, shows the 5 most recent sessions for instant
 * keyboard navigation — the most common use case for the palette.
 */
export const CommandPalette = memo(function CommandPalette({
  isOpen,
  onClose,
  onSelectSession,
  getToken,
  agents,
  recentSessions,
  scopeKey,
  useSearch = useSessionSearch,
}: CommandPaletteProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  // Reset the query whenever the palette opens/closes so the user starts
  // fresh each time.
  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  const { titleResults, contentResults, titleLoading, contentLoading,
    titleError, contentError, hasMore, loadMore, limitedCoverage, retryContent } =
    useSearch(query, getToken, recentSessions, scopeKey);

  const agentMap = useMemo(() => {
    const map = new Map<string, Agent>();
    for (const a of agents) map.set(a.id, a);
    return map;
  }, [agents]);

  // When query is empty, show recent sessions for quick navigation
  const recentToShow = useMemo(
    () => (!query.trim() && recentSessions
      ? recentSessions.filter((s) => !s.is_archived && s.session_type !== "sub_agent"
        && (!s.parent_session_id || s.session_type === "thread") && s.can_read !== false).slice(0, 5)
      : []),
    [query, recentSessions],
  );

  const handleSelect = useCallback(
    (id: string) => {
      onSelectSession(id);
      onClose();
    },
    [onSelectSession, onClose],
  );

  const hasAnyResults = titleResults.length > 0 || contentResults.length > 0;

  return (
    <CommandDialog open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* Visually hidden title + description for Radix Dialog accessibility.
          Without these, Radix logs warnings about missing DialogTitle /
          DialogDescription. They are not visible to sighted users. */}
      <DialogTitle className="sr-only">{t("commandPalette.placeholder")}</DialogTitle>
      <DialogDescription className="sr-only">{t("commandPalette.scopeHint")}</DialogDescription>
      <CommandInput
        placeholder={t("commandPalette.placeholder")}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {(titleLoading || contentLoading) && (
          <div role="status" className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
            {t(titleLoading && hasMore ? "commandPalette.moreResultsLoading" : "commandPalette.searching")}
          </div>
        )}

        {titleError && query.trim() && (
          <div role="alert" className="px-4 py-2 text-sm text-muted-foreground">
            {t(titleResults.length ? "commandPalette.loadedOnly" : "commandPalette.sessionSearchUnavailable")}
          </div>
        )}
        {limitedCoverage && query.trim() && !titleError && (
          <div role="note" className="px-4 py-2 text-xs text-muted-foreground">
            {t("commandPalette.legacyCoverage")}
          </div>
        )}
        {contentError && query.trim() && (
          <div role="alert" className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-muted-foreground">
            <span>{t(contentError === "timeout" ? "commandPalette.messageSearchTimeout"
              : contentError === "olderServer" ? "commandPalette.messageSearchUnavailable" : "commandPalette.messageSearchFailed")}</span>
            {contentError !== "olderServer" && <button type="button" onClick={retryContent}>
              {t("retry", { ns: "common" })}
            </button>}
          </div>
        )}
        {!titleLoading && !contentLoading && !hasAnyResults && !titleError && !contentError && query.trim() && (
          <CommandEmpty>{t("commandPalette.noSessionsOrMessages")}</CommandEmpty>
        )}
        {!contentLoading && !contentError && !contentResults.length && titleResults.length > 0 && query.trim() && (
          <div role="status" className="px-4 py-2 text-xs text-muted-foreground">
            {t("commandPalette.noMessageMatches")}
          </div>
        )}

        {/* Recent sessions (empty query) */}
        {!query.trim() && recentToShow.length > 0 && (
          <CommandGroup heading={t("commandPalette.recent")}>
            {recentToShow.map((s) => (
              <CommandItem
                key={s.id}
                value={`${s.id} ${s.title}`}
                onSelect={() => handleSelect(s.id)}
              >
                <Search className="size-4 text-muted-foreground" />
                <span className="flex-1 truncate">{s.title}</span>
                <span className="text-xs text-muted-foreground">
                  {formatRelativeDate(s.last_active_at)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Title matches */}
        {titleResults.length > 0 && (
          <CommandGroup heading={t("commandPalette.sessions")}>
            {titleResults.map((r) => (
              <SearchResultItem
                key={r.id}
                result={r}
                query={query}
                agentName={agentMap.get(r.agent_id ?? "")?.name}
                onSelect={handleSelect}
              />
            ))}
          </CommandGroup>
        )}

        {/* Content matches */}
        {contentResults.length > 0 && (
          <CommandGroup heading={t("commandPalette.messages")}>
            {contentResults.map((r) => (
              <SearchResultItem
                key={r.id}
                result={r}
                query={query}
                agentName={agentMap.get(r.agent_id ?? "")?.name}
                onSelect={handleSelect}
              />
            ))}
          </CommandGroup>
        )}
        {hasMore && query.trim() && (
          <button type="button" className="w-full px-4 py-2 text-left text-sm text-muted-foreground hover:bg-accent"
            onClick={loadMore} disabled={titleLoading}>
            {t(titleLoading ? "commandPalette.moreResultsLoading" : "commandPalette.moreTitles")}
          </button>
        )}
      </CommandList>
    </CommandDialog>
  );
});


/* ------------------------------------------------------------------ */
/*  SearchResultItem (memoized)                                        */
/* ------------------------------------------------------------------ */

interface SearchResultItemProps {
  result: SessionSearchResult;
  query: string;
  agentName?: string;
  onSelect: (id: string) => void;
}

/**
 * A single search result row inside the command palette.
 *
 * For title matches: shows the session title (highlighted) + date.
 * For content matches: shows the session title + a snippet of the matching
 * message (highlighted) + date.
 */
const SearchResultItem = memo(function SearchResultItem({
  result,
  query,
  agentName,
  onSelect,
}: SearchResultItemProps) {
  const { t } = useTranslation();
  const isContent = result.match_type === "content";
  return (
    <CommandItem
      value={`${result.id} ${result.title}`}
      onSelect={() => onSelect(result.id)}
    >
      {isContent ? (
        <MessageSquare className="size-4 text-muted-foreground" />
      ) : (
        <Search className="size-4 text-muted-foreground" />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm">
          {highlightMatch(result.title, query)}
        </span>
        {isContent && result.snippet && (
          <>
            <span className="truncate text-xs text-muted-foreground" aria-label={t("commandPalette.matchContext", { title: result.title })}>
              {highlightMatch(result.snippet, query, result.match_start, result.match_end)}
            </span>
            {result.role && <span className="text-xs text-muted-foreground">
              {t(result.role === "user" ? "commandPalette.roleUser" : "commandPalette.roleAssistant")}
              {result.message_at && ` · ${formatRelativeDate(result.message_at)}`}
            </span>}
          </>
        )}
      </div>
      {agentName && (
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          {agentName}
        </span>
      )}
      <span className="shrink-0 text-xs text-muted-foreground">
        {formatRelativeDate(result.last_active_at)}
      </span>
    </CommandItem>
  );
});
